import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Box,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Tooltip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const CATEGORIES = [
  'Wellness',
  'Therapeutic',
  'Luxury',
  'Detox',
  'Recovery',
  'Fitness',
  'Ayurveda',
  'Other'
];

const AddonPackageBuilder = ({ open, onClose, packageToEdit, onSaved }) => {
  // Package details
  const [packageName, setPackageName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState('Wellness');
  
  // Discount configuration
  const [discountType, setDiscountType] = useState('none'); // 'none', 'percentage', 'amount'
  const [discountValue, setDiscountValue] = useState(0);
  const [taxRate, setTaxRate] = useState(18.0);
  
  // Treatments (flat list with day_number for each)
  const [treatments, setTreatments] = useState([]);
  
  // Available treatments from API
  const [availableTreatments, setAvailableTreatments] = useState([]);
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Pricing preview
  const [pricingPreview, setPricingPreview] = useState(null);

  // Load treatments on mount
  useEffect(() => {
    const init = async () => {
      if (open) {
        const treatments = await loadTreatments(); // Get treatments
        if (packageToEdit) {
          loadPackageData(packageToEdit, treatments); // Pass treatments directly
        } else {
          resetForm();
        }
      }
    };
    init();
  }, [open, packageToEdit]);

  // Calculate pricing whenever treatments or discount changes
  useEffect(() => {
    calculatePricing();
  }, [treatments, discountType, discountValue, taxRate]);

  const loadTreatments = async () => {
    try {
      const response = await apiGet('/api/admin/treatments');
      if (response.ok) {
        const treatments = await response.json();
        const treatmentsArray = Array.isArray(treatments) ? treatments : [];
        setAvailableTreatments(treatmentsArray);
        return treatmentsArray; // Return the treatments for immediate use
      }
    } catch (err) {
      console.error('Failed to load treatments:', err);
      setError('Failed to load treatments');
    }
    return []; // Return empty array on error
  };

  const loadPackageData = async (pkg, treatmentsArray = null) => {
    try {
      setLoading(true);
      const response = await apiGet(`/api/addon-packages/${pkg.id}`);
      if (response.ok) {
        const data = await response.json();
        
        setPackageName(data.name);
        setDescription(data.description || '');
        setNotes(data.notes || '');
        setCategory(data.category || 'Wellness');
        setDiscountType(data.default_discount_type || 'none');
        setDiscountValue(data.default_discount_value || 0);
        setTaxRate(data.tax_rate || 18.0);
        
        // Use passed treatments array or fall back to state
        const treatmentsList = data.items.map(item => {
          // Find the treatment to get current price - case-insensitive matching
          const treatments = treatmentsArray || availableTreatments;
          const treatmentDetails = treatments.find(t => 
            t.name.toUpperCase() === item.treatment_name.toUpperCase()
          );
          return {
            day_number: item.day_number,
            treatment_name: item.treatment_name,
            duration: item.duration,
            base_price: treatmentDetails ? (treatmentDetails.base_price || treatmentDetails.tier_value || 0) : 0,
            tier_value: treatmentDetails ? (treatmentDetails.tier_value || 0) : 0
          };
        });
        
        setTreatments(treatmentsList);
      }
    } catch (err) {
      console.error('Failed to load package:', err);
      setError('Failed to load package data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPackageName('');
    setDescription('');
    setNotes('');
    setCategory('Wellness');
    setDiscountType('none');
    setDiscountValue(0);
    setTaxRate(18.0);
    setTreatments([]);
    setError(null);
    setPricingPreview(null);
  };

  const calculatePricing = () => {
    let treatmentsSubtotal = 0;
    
    // Sum up all treatment prices
    treatments.forEach(treatment => {
      if (treatment.treatment_name && treatment.base_price !== undefined) {
        treatmentsSubtotal += parseFloat(treatment.base_price) || 0;
      }
    });
    
    // Calculate discount
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = treatmentsSubtotal * (parseFloat(discountValue) / 100);
    } else if (discountType === 'amount') {
      discountAmount = parseFloat(discountValue) || 0;
    }
    
    // Calculate final amounts
    const subtotalAfterDiscount = Math.max(0, treatmentsSubtotal - discountAmount);
    const taxAmount = subtotalAfterDiscount * (parseFloat(taxRate) / 100);
    const total = subtotalAfterDiscount + taxAmount;
    
    setPricingPreview({
      treatmentsSubtotal,
      discountAmount,
      subtotalAfterDiscount,
      taxAmount,
      total
    });
  };

  const addTreatment = () => {
    setTreatments([...treatments, {
      day_number: 1,
      treatment_name: '',
      duration: 50,
      base_price: 0,
      tier_value: 0
    }]);
  };

  const removeTreatment = (treatmentIndex) => {
    setTreatments(treatments.filter((_, idx) => idx !== treatmentIndex));
  };

  const updateTreatment = (treatmentIndex, field, value) => {
    const newTreatments = [...treatments];
    
    if (field === 'treatment_name') {
      // Find treatment details - case-insensitive matching
      const treatment = availableTreatments.find(t => 
        t.name.toUpperCase() === value.toUpperCase()
      );
      if (treatment) {
        newTreatments[treatmentIndex] = {
          ...newTreatments[treatmentIndex],
          treatment_name: value,
          duration: treatment.duration || 50,
          base_price: treatment.base_price || treatment.tier_value || 0,
          tier_value: treatment.tier_value || 0
        };
      }
    } else {
      newTreatments[treatmentIndex][field] = value;
    }
    
    setTreatments(newTreatments);
  };

  const handleSave = async () => {
    // Validation
    if (!packageName.trim()) {
      setError('Package name is required');
      return;
    }
    
    // Check if at least one treatment exists
    if (treatments.length === 0) {
      setError('Add at least one treatment to the package');
      return;
    }
    
    // Check if all treatments are selected and have days
    const allTreatmentsValid = treatments.every(t => t.treatment_name && t.day_number);
    if (!allTreatmentsValid) {
      setError('Please select a treatment and day for all items');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Build items array - group by day and add order_in_day
      const treatmentsByDay = {};
      treatments.forEach(t => {
        if (!treatmentsByDay[t.day_number]) {
          treatmentsByDay[t.day_number] = [];
        }
        treatmentsByDay[t.day_number].push(t);
      });
      
      const items = [];
      Object.keys(treatmentsByDay).sort((a, b) => parseInt(a) - parseInt(b)).forEach(dayNum => {
        treatmentsByDay[dayNum].forEach((treatment, idx) => {
          items.push({
            day_number: parseInt(dayNum),
            treatment_name: treatment.treatment_name,
            duration: treatment.duration,
            order_in_day: idx + 1
          });
        });
      });
      
      const payload = {
        name: packageName,
        description: description,
        notes: notes,
        category: category,
        default_discount_type: discountType === 'none' ? null : discountType,
        default_discount_value: discountType === 'none' ? 0 : parseFloat(discountValue),
        tax_rate: parseFloat(taxRate),
        is_reusable: true, // Always reusable for admin-created packages
        created_by: 'Admin', // TODO: Get from user session
        created_by_role: 'admin',
        items: items
      };
      
      let response;
      if (packageToEdit) {
        // Update existing
        response = await apiPut('/api/addon-packages/${packageToEdit.id}', payload);
      } else {
        // Create new
        response = await apiPost('/api/addon-packages', payload);
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save package');
      }
      
      const result = await response.json();
      
      alert(packageToEdit 
        ? `Package "${packageName}" updated successfully!`
        : `Package "${packageName}" created successfully!\n\nEstimated Price: ₹${result.estimated_pricing?.total?.toFixed(2) || '0.00'}`
      );
      
      if (onSaved) {
        onSaved(result);
      }
      
      onClose();
      resetForm();
      
    } catch (err) {
      console.error('Error saving package:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#E4C984', 
        color: '#171717',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          {packageToEdit ? 'Edit Addon Package' : 'Addon Package Builder'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Two-Column Layout: 30% Left (Details) / 70% Right (Treatments) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '30% 68%', gap: '2%', mt: 2 }}>
              
              {/* LEFT PANEL - Package Details & Discount */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Package Details */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                    PACKAGE DETAILS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Package Name *"
                      value={packageName}
                      onChange={(e) => setPackageName(e.target.value)}
                      placeholder="e.g., Detox Booster"
                      variant="outlined"
                    />
                    
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        label="Category"
                      >
                        {CATEGORIES.map(cat => (
                          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Brief description..."
                      multiline
                      rows={2}
                      variant="outlined"
                    />

                    <TextField
                      fullWidth
                      label="Notes (Internal Use)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="When/how to use this package..."
                      multiline
                      rows={2}
                      variant="outlined"
                      helperText="Internal notes about when/how to use this package"
                    />
                  </Box>
                </Box>

                {/* Discount Configuration */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                    PACKAGE DISCOUNT
                  </Typography>
                  <Box>
                    <RadioGroup
                      value={discountType}
                      onChange={(e) => {
                        setDiscountType(e.target.value);
                        if (e.target.value === 'none') setDiscountValue(0);
                      }}
                    >
                      <FormControlLabel value="none" control={<Radio size="small" />} label="No Discount" />
                      <FormControlLabel value="percentage" control={<Radio size="small" />} label="Percentage %" />
                      <FormControlLabel value="amount" control={<Radio size="small" />} label="Fixed Amount ₹" />
                    </RadioGroup>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <TextField
                        fullWidth
                        label={discountType === 'percentage' ? 'Discount (%)' : 'Discount Amount (₹)'}
                        type="number"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(Math.max(0, parseFloat(e.target.value) || 0))}
                        disabled={discountType === 'none'}
                        variant="outlined"
                        inputProps={{ min: 0, step: discountType === 'percentage' ? 1 : 100 }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Tax Rate (%)"
                        type="number"
                        value={taxRate}
                        onChange={(e) => setTaxRate(Math.max(0, parseFloat(e.target.value) || 0))}
                        variant="outlined"
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* RIGHT PANEL - Treatments Only */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#374151' }}>
                  TREATMENTS BY DAY
                </Typography>

                {/* Treatments List */}
                <Box sx={{ mb: 1, pt: 1 }}>
                  {treatments.length === 0 ? (
                    <Alert 
                      severity="info" 
                      sx={{ 
                        fontSize: '0.875rem',
                        backgroundColor: '#fffbeb',
                        color: '#78350f',
                        '& .MuiAlert-icon': {
                          color: '#E4C984'
                        }
                      }}
                    >
                      No treatments added. Click "Add Treatment" below to start.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {treatments.map((treatment, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '80px 1fr 120px 80px 40px',
                            gap: 1,
                            alignItems: 'center'
                          }}
                        >
                          {/* Day Number */}
                          <TextField
                            size="small"
                            label="Day"
                            type="number"
                            value={treatment.day_number || 1}
                            onChange={(e) => updateTreatment(index, 'day_number', parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, max: 30 }}
                            variant="outlined"
                          />

                          {/* Treatment Selection */}
                          <Autocomplete
                            size="small"
                            options={availableTreatments}
                            getOptionLabel={(option) => option.name || ''}
                            value={availableTreatments.find(t => 
                              t.name.toUpperCase() === treatment.treatment_name.toUpperCase()
                            ) || null}
                            onChange={(e, newValue) => {
                              if (newValue) {
                                updateTreatment(index, 'treatment_name', newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Select Treatment" variant="outlined" />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.duration} min • ₹{option.base_price || option.tier_value || 0} • {option.type || 'N/A'}{option.tier_level && ` • ${option.tier_level}`}
                                  </Typography>
                                </Box>
                              </li>
                            )}
                          />

                          {/* Duration */}
                          <TextField
                            size="small"
                            label="Duration"
                            type="number"
                            value={treatment.duration}
                            onChange={(e) => updateTreatment(index, 'duration', parseInt(e.target.value) || 50)}
                            inputProps={{ min: 15, max: 240, step: 5 }}
                            variant="outlined"
                          />

                          {/* Price */}
                          <Chip
                            label={`₹${treatment.base_price || 0}`}
                            size="small"
                            sx={{ backgroundColor: '#E4C984', color: '#171717' }}
                          />

                          {/* Delete Button */}
                          <IconButton
                            size="small"
                            onClick={() => removeTreatment(index)}
                            sx={{ color: '#dc2626' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Add Treatment Button - Outside scroll area */}
                <Button
                  variant="outlined"
                  onClick={addTreatment}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    borderColor: '#E4C984',
                    color: '#E4C984',
                    '&:hover': {
                      borderColor: '#d4b874',
                      backgroundColor: 'rgba(228, 201, 132, 0.1)'
                    }
                  }}
                >
                  Add Treatment
                </Button>
              </Box>
              {/* End RIGHT PANEL */}

            </Box>
            {/* End Two-Column Grid */}

            {/* Pricing Preview - Full Width Below Both Columns */}
            {pricingPreview && (
              <Box sx={{ mt: 1, pt: 2, borderTop: '2px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  PRICING PREVIEW
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: '#fffbeb',
                    border: '2px solid #E4C984'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Treatments Subtotal:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₹{pricingPreview.treatmentsSubtotal.toFixed(2)}
                      </Typography>
                    </Box>
                    
                    {discountType !== 'none' && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                        <Typography variant="body2">
                          Package Discount ({discountType === 'percentage' ? `${discountValue}%` : `₹${discountValue}`}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          -₹{pricingPreview.discountAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">After Discount:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₹{pricingPreview.subtotalAfterDiscount.toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Tax ({taxRate}%):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        +₹{pricingPreview.taxAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    
                        <Divider sx={{ my: 0.5 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" sx={{ color: '#78350f' }}>Total:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#E4C984' }}>
                        ₹{pricingPreview.total.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f9fafb' }}>
        <Button onClick={onClose} disabled={saving}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{
            backgroundColor: '#E4C984',
            color: '#171717',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#d4b874'
            }
          }}
        >
          {saving ? 'SAVING...' : 'SAVE PACKAGE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddonPackageBuilder;

